import React, { createContext, useState, useContext, useEffect } from "react";
import authService from "../services/authService";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check for saved user on mount
    const savedUser = authService.getCurrentUser();
    if (savedUser) {
      setUser(savedUser);
    }
    setLoading(false);
  }, []);

  const login = async (email, password, rememberMe) => {
    try {
      setError(null);
      setLoading(true);
      const userData = await authService.login(email, password);

      if (!rememberMe) {
        // If remember me is not checked, don't persist the user data
        sessionStorage.setItem("user", JSON.stringify(userData));
      }

      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message || "Failed to login");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    authService.logout();
    sessionStorage.removeItem("user");
    setUser(null);
  };

  const register = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const response = await authService.register(email, password);
      return response;
    } catch (err) {
      setError(err.message || "Failed to register");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      setError(null);
      setLoading(true);
      const response = await authService.requestPasswordReset(email);
      return response;
    } catch (err) {
      setError(err.message || "Failed to request password reset");
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        logout,
        register,
        requestPasswordReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
